import axios from 'axios';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import React, {useEffect, useState, useContext} from 'react';

import {ModalContext} from 'app/app';
import {Download} from 'feather-icons-react';
import CW from './images/coverwhale_logo_white.png';
import {IconedButton} from 'components/IconedButton';

// Import carrier logos
import coverWhaleLogo from "../../../assets/coverwhale_logo_white.png";
import progressiveLogo from "../../../assets/logo-progressive.svg";
import nirvanaLogo from "../../../assets/Nirvana.png";
import {Icon, Loader, useAPI, ViewContext} from 'components/lib';
import {Form, Formik} from 'formik';
import {Edit} from 'feather-icons-react/build/IconComponents';
import DateUtils from 'helpers/dateUtils';
import ReactQuill from 'react-quill';
import { TemplateButton } from 'components/TemplateButton';

import styles from './styles.module.scss';
import 'react-quill/dist/quill.snow.css';
import '../../../css/quillCustom.css';
import {EditableField} from "../../../components/editableField";

const dateUtils = new DateUtils();

// Function to get the appropriate logo based on selected carrier
const getCarrierLogo = (carrier) => {
  switch (carrier) {
    case "Cover Whale":
      return coverWhaleLogo;
    case "Progressive":
      return progressiveLogo;
    case "Nirvana":
      return nirvanaLogo;
    default:
      return coverWhaleLogo; // Default to Cover Whale logo
  }
};

const renewalIssue = [
  {value: 'Insured Payment Needed'},
  {value: '3 years loss runs'},
  {value: "MVR's for all drivers"},
  {value: 'DOT Safety Manual'},
  {value: "Driver Count on SAFER Doesn't Match"},
  {value: "Units on SAFER Don't Match"},
  {value: 'Ineligible Cargo On SAFER'},
  {value: 'Address On Policy different than SAFER'},
  {value: 'Detailed/Precise Items the insured is Hauling'},
  {value: 'Ineligible Cargo'},
  {value: 'Ineligible Operation'},
  {value: 'FMCSA Scores High'},
  {value: 'Insured is linked to 1 or more other entities'},
  {value: 'Proof of Companion Policy Needed'},
  {value: 'Insured and/or agent needs to sign policy docs'},
  {value: 'Other'},
];

export default function RenewalDetails() {
  const {id} = useParams();
  const navigate = useNavigate();
  const context = useContext(ViewContext);
  const {setModal} = useContext(ModalContext);
  const user = useAPI('/api/user');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [renewalData, setRenewalData] = useState(null);
  const [selectedIssues, setSelectedIssues] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const previousPage = searchParams.get("page") || 1;
  const [tempPremium, setTempPremium] = useState("");
  const [agenciesOptions, setAgenciesOptions] = useState([]);
  const [companiesOptions, setCompaniesOptions] = useState([]);
  const [agencyNames, setAgencyNames] = useState({});
  const [companyIds, setCompanyIds] = useState({});
  const [companyEmails, setCompanyEmails] = useState({});
  const [companyNames, setCompanyNames] = useState({});


  async function fetchRenewalData() {
    setLoading(true);
    await axios
      .get(`/api/renewals/admin/${id}`)
      .then(async (res) => {
        const renewalData = res.data.data;
        setRenewalData(renewalData);
        if (renewalData.renewalIssues) {
          setSelectedIssues(renewalData.renewalIssues);
        }
      })
      .catch((err) => {
        setError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  const fetchDropdownData = async () => {
    try {
      // Run both API calls in parallel for better performance
      const [companiesResponse, agenciesResponse] = await Promise.all([
        axios.get("/api/company/all"),
        axios.get("/api/account/all/minimal")
      ]);

      // Process companies data
      const companies = companiesResponse.data.data;
      const sortedCompanies = companies
        .map((company) => ({
          label: company.name.trim(),
          value: company.name,
        }))
        .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));

      setCompaniesOptions(sortedCompanies);

      const emails = companies.reduce((acc, company) => {
        acc[company.name] = company.email;
        return acc;
      }, {});

      const ids = companies.reduce((acc, company) => {
        acc[company.name] = company.id;
        return acc;
      }, {});

      const names = companies.reduce((acc, company) => {
        acc[company.name] = company.name;
        return acc;
      }, {});

      setCompanyEmails(emails);
      setCompanyIds(ids);
      setCompanyNames(names);

      // Process agencies data
      const agencies = agenciesResponse.data.data;
      const sortedAgencies = agencies
        .map((agency) => ({
          label: agency.name.trim(),
          value: agency.id,
        }))
        .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));

      setAgenciesOptions(sortedAgencies);

      const agencyNames = agencies.reduce((acc, agency) => {
        acc[agency.id] = agency.name;
        return acc;
      }, {});
      setAgencyNames(agencyNames);

    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  };

  useEffect(() => {
    fetchRenewalData();
    fetchDropdownData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleIssueChange = (issue) => {
    setSelectedIssues((prevSelectedIssues) => {
      const issueObject = {value: issue};
      if (prevSelectedIssues.some((i) => i.value === issue)) {
        return prevSelectedIssues.filter((i) => i.value !== issue);
      } else {
        return [...prevSelectedIssues, issueObject];
      }
    });
  };

  const formatCurrency = (value) => {
    const numericValue = parseFloat(value.toString().replace(/[^0-9.]/g, '')) || 0;
    return numericValue.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  };

  const handleNextClick = async () => {
    if (selectedIssues.length === 0) {
      context.notification.show(
        'Please select at least one issue before continuing.',
        'error',
        true,
        'toast',
        'x-circle'
      );
      return;
    } else {
      await axios
        .put(`/api/renewals/${id}`, {renewalIssues: selectedIssues})
        .then(() => {
          context.notification.show(
            'Issues sent successfully!',
            'success',
            true,
            'toast',
            'check-circle'
          );
        })
        .catch((error) => {
          context.notification.show(
            'There was a problem when sending the selected issues.',
            'error',
            true,
            'toast',
            'x-circle'
          );
          console.error('Error sending issues: ', error);
        })
        .finally(() => {
          setModal({
            name: 'renewal_issue_modal',
            data: {
              assignedAgentEmail: renewalData.assignedAgentEmail,
            },
          });
        });
    }
  };

  useEffect(() => {
    if (
      renewalData &&
      (renewalData.renewalIssues !== undefined || null)
    ) {
      if (
        renewalData.renewalIssues.length > 0 &&
        !renewalData.messages
      ) {
        setModal({
          name: 'ticket_issue_modal',
          data: {
            assignedAgentEmail: renewalData.assignedAgentEmail,
          },
        });
      }
    }
  }, [setModal, renewalData]);

  const handleSubmit = async (values) => {
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('sender', 'Cover Badger Renewal Team');
      formData.append('receiver', renewalData.assignedAgentEmail);
      formData.append('title', values.title);
      formData.append('description', values.description);
      [...selectedFiles].forEach((file, i) => {
        formData.append(`documents`, file, file.name);
      });

      const response = await axios.put(
        `/api/renewals/${id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      if (response.data.success) {
        context.notification.show(
          'Message sent successfully!',
          'success',
          true,
          'toast',
          'check-circle'
        );
        setModal('');
      }
    } catch (error) {
      setSending(false);
      context.notification.show(
        error.message ||
        'There was a problem when sending the message.',
        'error',
        true,
        'toast',
        'x-circle'
      );
    } finally {
      fetchRenewalData();
      setSending(false);
    }
  };

  const handleUpdateField = async (field, value) => {
    try {
      const response = await axios.put(`/api/renewals/${id}`, {
        [field]: value,
      });

      if (response.status !== 200) {
        throw new Error('Failed to update field');
      }

      setRenewalData((prevData) => ({
        ...prevData,
        [field]: value,
      }));
    } catch (error) {
      context.notification.show(
        'There was a problem when updating the field.',
        'error',
        true,
        'toast',
        'x-circle'
      );
    }
  };

  const handleEmailUpdate = async (field, selectedValue) => {
    let emailValue = '';

    if (field === 'agency') {
      const agencyId = selectedValue;
      if (!agencyId) return;

      try {
        console.log("Fetching agent email for agencyId:", agencyId);
        const {data} = await axios.get(`/api/account/${agencyId}`);
        const agentEmail = data.data.owner_email;
        console.log("Fetched agent email:", agentEmail);

        await handleUpdateField('assignedAgentEmail', agentEmail);
      } catch (error) {
        console.warn('Error fetching agent email:', error);
      }
    } else if (field === 'insured') {
      emailValue = companyEmails[selectedValue];
      if (emailValue) {
        await handleUpdateField('assignedUserEmail', emailValue);
      } else {
        console.warn('Email not found for this insured.');
      }
    }
  };

  const handleUpdateRenewalIssue = async (issueId, solved) => {
    try {
      const updatedIssues = renewalData.renewalIssues.map((issue) =>
        issue.value === issueId ? {...issue, solved} : issue
      );

      const response = await axios.put(`/api/renewals/${id}`, {
        renewalIssues: updatedIssues,
      });

      if (response.status !== 200) {
        throw new Error('Failed to update renewal issue');
      }

      setRenewalData((prevData) => ({
        ...prevData,
        renewalIssues: updatedIssues,
      }));
      window.location.reload();
    } catch (error) {
      context.notification.show(
        'There was a problem when updating the issue.',
        'error',
        true,
        'toast',
        'x-circle'
      );
    }
  };

  if (user.loading || loading) return <Loader/>;
  if (error) {
    return <div>Error loading renewal data: {error.message}</div>;
  }
  if (!renewalData) {
    return <div>No renewal data available</div>;
  }

  return (
    <>
      {renewalData &&
      (renewalData.renewalIssues.length === 0 ||
        !renewalData.messages) ? (
        <>
          <div className="w-full flex items-center justify-left">
            <IconedButton
              size="130px"
              color="#d32425"
              textColor="white"
              icon="arrow-left"
              text="Go back"
              action={() => {
                navigate(`/admin/renewal?page=${previousPage}`);
              }}
            />
          </div>

          <div
            className="mb-6 xl:w-1/2 sm:w-3/4 xs:w-full mx-auto bg-white pb-6 max-w-screen-xl rounded-[12px]"
            id="14"
          >
            <header className="bg-black flex items-center justify-center px-4 py-4 rounded-t-[12px]" style={{ minHeight: "80px" }}>
              <img 
                src={getCarrierLogo(renewalData.carrier)} 
                alt={`${renewalData.carrier} Logo`} 
                style={{
                  maxHeight: "60px",
                  maxWidth: "360px",
                  height: "auto",
                  width: "auto",
                  objectFit: "contain",
                  filter: renewalData.carrier === "Nirvana" ? "brightness(0) invert(1)" : "none"
                }}
              />
            </header>

            <div className="flex-col border rounded p-4 mt-4 pt-0 border-slate-300 mx-6">
              <div className="flex justify-between py-2 border-b border-slate-300">
                <div>Renewal Number</div>
                <div>{renewalData.renewalNumber}</div>
              </div>
              <EditableField
                label="Submission / Policy"
                value={renewalData.submissionNumber}
                onSave={(value) => handleUpdateField('submissionNumber', value)}
              />
              <EditableField
                label="Agency Name"
                value={renewalData.agencyName}
                onSave={(newAgencyId) => {
                  const selectedAgency = agenciesOptions.find((agency) => agency.value === newAgencyId);
                  if (selectedAgency) {
                    handleUpdateField('agencyName', selectedAgency.label);
                    handleEmailUpdate('agency', newAgencyId);
                    setRenewalData((prev) => ({
                      ...prev,
                      agencyId: newAgencyId,
                      agencyName: selectedAgency.label,
                    }));
                  }
                }}
                options={agenciesOptions.map((agency) => ({
                  label: agency.label,
                  value: agency.value,
                }))}
              />
              <EditableField
                label="Agent Email"
                value={renewalData.assignedAgentEmail}
                onSave={(value) => handleUpdateField('assignedAgentEmail', value)}
              />
              <div className="flex justify-between py-2 border-b border-slate-300">
                <div>Agency Phone</div>
                <div>{renewalData.agencyPhone || "N/A"}</div>
              </div>
              <EditableField
                label="Name of Insured"
                value={renewalData.companyName}
                onSave={(value) => handleUpdateField('companyName', value)}
                options={companiesOptions.map((company) => ({
                  label: company.label,
                  value: company.value,
                }))}
              />

              <EditableField
                label="Renewal Premium"
                value={formatCurrency(renewalData.renewalPremium) || '$0.00'}
                onSave={async (value) => {
                  const numericValue = parseFloat(value.replace(/[^0-9.]/g, "")) || 0;
                  try {
                    await axios.put(`/api/renewals/${id}`, {renewalPremium: numericValue});
                    setRenewalData((prev) => ({
                      ...prev,
                      renewalPremium: numericValue,
                    }));
                  } catch (error) {
                    console.error("Error saving renewalPremium:", error);
                  }
                }}
              />

              <EditableField
                label="Renewal Date"
                value={renewalData.renewalDate ? new Date(renewalData.renewalDate).toISOString().split("T")[0] : 'N/A'}
                onSave={async (value) => {
                  try {
                    const updatedDate = value || renewalData.renewalDate;
                    await axios.put(`/api/renewals/${id}`, {renewalDate: updatedDate});
                    setRenewalData((prev) => ({
                      ...prev,
                      renewalDate: updatedDate,
                    }));
                  } catch (error) {
                    console.error("Error saving renewal date:", error);
                  }
                }}
                isLastItem
              />
            </div>

            <div className="p-4 pl-6 mt-4 pt-0">
              Select issues that need to be addressed:
            </div>

            <div className="flex-col border rounded p-4 pt-0 border-slate-300 mx-6">
              {renewalIssue.map((issue, index) => (
                <div
                  key={index}
                  className="flex justify-between py-2 border-b border-slate-300"
                >
                  <div>{issue.value}</div>
                  <input
                    type="checkbox"
                    checked={selectedIssues.some(
                      (i) => i.value === issue.value
                    )}
                    onChange={() => handleIssueChange(issue.value)}
                  />
                </div>
              ))}
            </div>

            <div className="p-4 mt-4 pt-0">
              <div className="w-full flex items-center">
                <IconedButton
                  size="1500px"
                  color="#d32425"
                  textColor="white"
                  text="Next"
                  action={() => handleNextClick()}
                />
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="w-full flex items-center justify-left">
            <IconedButton
              size="130px"
              color="#d32425"
              textColor="white"
              icon="arrow-left"
              text="Go back"
              action={() => {
                navigate(`/admin/renewal?page=${previousPage}`);
              }}
            />
          </div>
          <main className="flex-1 flex flex-col md:flex-row gap-4 max-h-full mt-2">
            {/* left */}
            <div className="w-full md:w-1/2">
              <div
                className="mb-6 rounded-[12px] w-full mx-auto bg-white pb-6 max-w-screen-xl"
                id="14"
              >
                <header
                  className="bg-black flex items-center justify-center px-4 py-4 rounded-t-[12px]" style={{ minHeight: "80px" }}>
                  <img 
                    src={getCarrierLogo(renewalData.carrier)} 
                    alt={`${renewalData.carrier} Logo`} 
                    style={{
                      maxHeight: "60px",
                      maxWidth: "360px",
                      height: "auto",
                      width: "auto",
                      objectFit: "contain",
                      filter: renewalData.carrier === "Nirvana" ? "brightness(0) invert(1)" : "none"
                    }}
                  />
                </header>

                <div className="flex-col border rounded p-4 mt-4 pt-0 border-slate-300 mx-6">
                  <div className="flex justify-between py-2 border-b border-slate-300">
                    <div>Renewal Number</div>
                    <div>{renewalData.renewalNumber}</div>
                  </div>
                  <EditableField
                    label="Submission / Policy"
                    value={renewalData.submissionNumber}
                    onSave={(value) => handleUpdateField('submissionNumber', value)}
                  />
                  <EditableField
                    label="Agency Name"
                    value={renewalData.agencyName}
                    onSave={(newAgencyId) => {
                      const selectedAgency = agenciesOptions.find((agency) => agency.value === newAgencyId);
                      if (selectedAgency) {
                        handleUpdateField('agencyName', selectedAgency.label);
                        handleEmailUpdate('agency', newAgencyId);
                        setRenewalData((prev) => ({
                          ...prev,
                          agencyId: newAgencyId,
                          agencyName: selectedAgency.label,
                        }));
                      }
                    }}
                    options={agenciesOptions.map((agency) => ({
                      label: agency.label,
                      value: agency.value,
                    }))}
                  />
                  <EditableField
                    label="Agent Email"
                    value={renewalData.assignedAgentEmail}
                    onSave={(value) => handleUpdateField('assignedAgentEmail', value)}
                  />
                  <div className="flex justify-between py-2 border-b border-slate-300">
                    <div>Agency Phone</div>
                    <div>{renewalData.agencyPhone || "N/A"}</div>
                  </div>
                  <EditableField
                    label="Name of Insured"
                    value={renewalData.companyName}
                    onSave={(value) => handleUpdateField('companyName', value)}
                    options={companiesOptions.map((company) => ({
                      label: company.label,
                      value: company.value,
                    }))}
                  />

                  <EditableField
                    label="Renewal Premium"
                    value={formatCurrency(renewalData.renewalPremium) || '$0.00'}
                    onSave={async (value) => {
                      const numericValue = parseFloat(value.replace(/[^0-9.]/g, "")) || 0;
                      try {
                        await axios.put(`/api/renewals/${id}`, {renewalPremium: numericValue});
                        setRenewalData((prev) => ({
                          ...prev,
                          renewalPremium: numericValue,
                        }));
                      } catch (error) {
                        console.error("Error saving renewalPremium:", error);
                      }
                    }}
                  />

                  <EditableField
                    label="Renewal Date"
                    value={renewalData.renewalDate ? new Date(renewalData.renewalDate).toISOString().split("T")[0] : 'N/A'}
                    onSave={async (value) => {
                      try {
                        const updatedDate = value || renewalData.renewalDate;
                        await axios.put(`/api/renewals/${id}`, {renewalDate: updatedDate});
                        setRenewalData((prev) => ({
                          ...prev,
                          renewalDate: updatedDate,
                        }));
                      } catch (error) {
                        console.error("Error saving renewal date:", error);
                      }
                    }}
                    isLastItem
                  />
                </div>

                <div className="p-4 pl-6 mt-4 pt-0">
                  Check an issue to mark it as solved:
                </div>

                <div className="flex-col border rounded p-4 pt-0 border-slate-300 mx-6">
                  {renewalData.renewalIssues.map((issue, index) => (
                    <div
                      key={index}
                      className="flex justify-between py-2 border-b border-slate-300"
                    >
                      <div className="inline-flex items-center">
                        {issue.value}
                      </div>
                      <div className="inline-flex items-center">
                        <label className={styles.label}>
                          <span className={styles.checkbox_container}>
                            <input
                              type="checkbox"
                              className={styles.checkbox}
                              checked={issue.solved}
                              onChange={(e) => {
                                const isChecked = e.target.checked;
                                handleUpdateRenewalIssue(
                                  issue.value,
                                  isChecked
                                );
                              }}
                              disabled={
                                renewalData.ticketStatus ===
                                'Completed'
                              }
                            />
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className={styles.icon}
                              viewBox="0 0 20 20"
                              fill="currentColor"
                              stroke="currentColor"
                              stroke-width="1"
                            >
                              <path
                                fill-rule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clip-rule="evenodd"
                              ></path>
                            </svg>
                          </span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>

                {renewalData.ticketStatus === 'Completed' ? null : (
                  <div className="p-4 mt-4 pt-0">
                    <div className="w-full flex items-center">
                      <IconedButton
                        size="1500px"
                        color="#d32425"
                        textColor="white"
                        text="Add"
                        action={() => {
                          setModal({
                            name: 'add_renewal_issue_modal',
                            data: {
                              status: renewalData.status,
                              renewalIssues: renewalData.renewalIssues,
                            },
                          });
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* right */}
            <div className="w-full md:w-1/2">
              <div class="w-full bg-white flex items-center justify-center rounded-[12px]">
                <div className="w-full mt-[20px] mb-[20px]">
                  <div className="max-h-[800px] flex flex-col">
                    <div
                      className="w-full flex justify-between items-center mb-4 pb-2 px-4 border-b border-slate-300">
                      <div>
                        <p className="font-bold">
                          {renewalData.agencyName}
                        </p>
                        <p className="text-sm">
                          {renewalData.assignedAgentEmail}
                        </p>
                        <p className="text-sm">
                          {dateUtils.timestamp(renewalData.updatedAt)}
                        </p>
                      </div>

                      {renewalData.ticketStatus ===
                      'Completed' ? null : (
                        <button
                          className="bg-[#d32425] text-white px-4 py-2 rounded-md disabled:bg-gray-200 disabled:cursor-not-allowed"
                          onClick={() => {
                            setModal({
                              name: 'renewal_status_modal',
                              data: {
                                id: renewalData.id,
                                status: renewalData.status,
                                ticketStatus: renewalData.ticketStatus,
                              },
                            });
                          }}
                          disabled={
                            renewalData.ticketStatus === 'Completed'
                          }
                        >
                          <div className="flex justify-between items-center gap-2">
                            <Icon
                              className="icon"
                              image="check-square"
                              size="18"
                              color="light"
                            />
                            Select Status
                          </div>
                        </button>
                      )}
                    </div>

                    {/* Ticket messages */}
                    <div className="flex-1 overflow-y-scroll px-4 py-2 flex flex-col-reverse">
                      {renewalData.messages
                        .slice()
                        .sort(
                          (a, b) =>
                            new Date(b.sendAt) - new Date(a.sendAt)
                        )
                        .map((message, index) => (
                          <div
                            key={index}
                            className="bg-gray-100 rounded-md p-2 mb-2 w-full"
                          >
                            <div id="title" className="mb-2 p-1">
                              Subject: {message.title}
                            </div>
                            <div
                              className="whitespace-pre-wrap p-1"
                              dangerouslySetInnerHTML={{__html: message.description}}
                            />
                            <br/>
                            <div className="w-full flex justify-between items-center p-1">
                              <div>{message.sender}</div>
                              <div>
                                {dateUtils.timestamp(message.sendAt)}
                              </div>
                            </div>
                            {message.documents.length > 0 && (
                              <div className="mt-2 mb-2">
                                <ul className="flex flex-wrap">
                                  {message.documents.map(
                                    (document, index) => {
                                      const urlParts =
                                        document.split('/');
                                      const fileNameWithTimestamp =
                                        urlParts[urlParts.length - 1];
                                      const fileName =
                                        fileNameWithTimestamp.split(
                                          '-'
                                        )[0];
                                      const decodedFileName =
                                        decodeURIComponent(fileName);
                                      const truncatedDocument =
                                        decodedFileName.length > 18
                                          ? decodedFileName.substring(
                                          0,
                                          18
                                        ) + '...'
                                          : decodedFileName;

                                      return (
                                        <label
                                          key={index}
                                          htmlFor="custom-input"
                                          className="
                                          text-md text-white mt-2 mr-2 py-2
                                          px-4 rounded-md border-0 bg-[#d32425]
                                          cursor-pointer max-w-[180px] flex
                                          justify-center items-center
                                        "
                                          title="click to download file"
                                        >
                                          <a
                                            href={document}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="
                                            text-white no-underline truncate
                                            flex items-center whitespace-nowrap
                                            overflow-hidden text-ellipsis
                                          "
                                          >
                                            <span
                                              style={{
                                                marginRight: '8px',
                                              }}
                                            >
                                              {truncatedDocument}
                                            </span>
                                            <Download
                                              className="text-white"
                                              size={16}
                                            />
                                          </a>
                                        </label>
                                      );
                                    }
                                  )}
                                </ul>
                              </div>
                            )}
                          </div>
                        ))}
                    </div>

                    {renewalData.ticketStatus ===
                    'Completed' ? null : (
                      <Formik
                        initialValues={{
                          title: '',
                          description: '',
                          documents: [],
                        }}
                        onSubmit={handleSubmit}
                      >
                        {({
                            setFieldValue,
                            handleChange,
                            values,
                            setFieldTouched
                          }) => (
                          <Form encType="multipart/form-data">
                            <div className="w-full mb-4 px-4 py-2">
                              <div className="flex justify-between items-center mb-2">
                                <input
                                  type="text"
                                  className="flex-1 p-2 border border-slate-300 rounded-lg mr-2"
                                  placeholder="Subject..."
                                  name="title"
                                  value={values.title}
                                  onChange={handleChange}
                                />
                                <TemplateButton 
                                  toolType="renewals"
                                  onTemplateSelect={(template) => {
                                    setFieldValue('title', template.title);
                                    // Convert newlines to HTML breaks for ReactQuill
                                    const formattedMessage = template.message.replace(/\n/g, '<br>');
                                    setFieldValue('description', formattedMessage);
                                  }}
                                />
                              </div>

                              <ReactQuill
                                value={values.description}
                                onChange={(content) => {
                                  setFieldValue('description', content);
                                  setFieldTouched('description', true, false);
                                }}
                                modules={{
                                  toolbar: [
                                    ['bold', 'italic', 'underline', 'strike'],
                                    [{'list': 'ordered'}, {'list': 'bullet'}],
                                    ['clean']
                                  ],
                                }}
                                formats={[
                                  'bold', 'italic', 'underline', 'strike',
                                  'list', 'bullet'
                                ]}
                                placeholder="Write a message"
                              />

                              <ul className="flex flex-wrap">
                                {Array.from(selectedFiles).map(
                                  (file) => (
                                    <label
                                      htmlFor="custom-input"
                                      className="
                                    block text-md text-white mt-2 mr-2 py-2 px-4
                                    rounded-md border-0 bg-[#d32425]
                                    cursor-pointer w-[200px]
                                    "
                                      title="Click to change file"
                                    >
                                      <div className="flex items-center">
                                        <span
                                          style={{
                                            marginRight: '8px',
                                          }}
                                        >
                                          {file.name.length > 18
                                            ? file.name.substring(
                                            0,
                                            18
                                          ) + '...'
                                            : file.name}
                                        </span>
                                        <Edit
                                          className="text-white"
                                          size={16}
                                        />
                                      </div>
                                    </label>
                                  )
                                )}
                              </ul>
                            </div>

                            {renewalData.ticketStatus ===
                            'Completed' ? null : (
                              <div className="w-full flex justify-between px-4 py-2">
                                <div className="flex flex-row items-center">
                                  <input
                                    type="file"
                                    id="custom-input"
                                    onChange={(event) => {
                                      setSelectedFiles(
                                        event.target.files
                                      );
                                    }}
                                    hidden
                                    multiple
                                    disabled={
                                      renewalData.ticketStatus ===
                                      'Completed'
                                    }
                                  />
                                  <label
                                    htmlFor="custom-input"
                                    className="
                                      block text-md text-white mr-4 py-2
                                      px-4 rounded-md border-0 bg-[#d32425]
                                      cursor-pointer disabled:cursor-not-allowed
                                      "
                                    disabled={
                                      renewalData.ticketStatus ===
                                      'Completed'
                                    }
                                  >
                                    Attach one or more documents
                                  </label>
                                </div>

                                <button
                                  className="
                                    bg-[#d32425] text-white
                                    px-4 py-2 rounded-md disabled:bg-gray-200
                                    disabled:cursor-not-allowed
                                  "
                                  type="submit"
                                  disabled={
                                    sending ||
                                    renewalData.ticketStatus ===
                                    'Completed' ||
                                    !values.title
                                  }
                                >
                                  {sending ? 'Sending...' : 'Send'}
                                </button>
                              </div>
                            )}
                          </Form>
                        )}
                      </Formik>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </main>
        </>
      )}
    </>
  );
}
